import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Star,
  Camera,
  MessageSquare,
  MapPin,
  Award,
  X,
  ExternalLink,
  ChevronRight,
  User,
  TrendingUp,
} from "lucide-react";

const GoogleDataModal = ({ open, onOpenChange, data, email, profilePicture, nome }) => {
  const [selectedTab, setSelectedTab] = useState("reviews");

  if (!data || !data.dados_google) {
    return null;
  }

  const googleData = data.dados_google;

  // Extrair informações do perfil (índice 16)
  const profileInfo = googleData[16] || [];
  const userName = profileInfo[0] || nome || "Usuário";
  const userDetails = profileInfo[1] || [];
  const userAvatar = userDetails[6] ? userDetails[6][0] : profilePicture;
  
  // Extrair estatísticas de contribuições
  const contributions = profileInfo[8] ? profileInfo[8][0] || [] : [];
  const contributionStats = contributions.map(item => ({
    type: item[6] || "",
    count: item[7] || 0,
  }));

  // Extrair informações de nível
  const levelInfo = profileInfo[8] ? profileInfo[8][1] || [] : [];
  const totalPoints = levelInfo[0] || 0;
  const currentLevel = levelInfo[1] || 0;
  const levelDetails = levelInfo[4] || [];
  const levelName = levelDetails[0] || `Nível ${currentLevel}`;

  // Extrair fotos e reviews
  const photosAndReviews = googleData[22] || [];
  const photosList = photosAndReviews[1] || [];
  const reviewsData = googleData[45]?.[0] || [];

  const allPhotos = [];
  const allReviews = [];

  // Extrair reviews de googleData[45]
  reviewsData.forEach((reviewItem) => {
    if (!reviewItem || !Array.isArray(reviewItem)) return;

    let placeName = "Local desconhecido";
    let placeAddress = "";
    let reviewText = "";
    let ownerResponse = "";
    let photoUrl = null;
    let placeMapLink = null;
    let rating = 0;

    if (reviewItem[4]?.[2]) placeName = reviewItem[4][2];
    else if (reviewItem[1]?.[0]?.[4]) placeName = reviewItem[1][0][4];

    if (reviewItem[4]?.[3]) placeAddress = reviewItem[4][3];
    if (reviewItem[4]?.[17]?.[0]) placeMapLink = reviewItem[4][17][0];

    if (reviewItem[4]?.[10]?.[0]?.[6]?.[0]) photoUrl = reviewItem[4][10][0][6][0];
    else if (reviewItem[1]?.[0]?.[6]?.[0]?.[2]?.[1]) photoUrl = reviewItem[1][0][6][0][2][1];

    if (reviewItem[2]?.[2]?.[15]?.[0]?.[0]) reviewText = reviewItem[2][2][15][0][0];
    if (reviewItem[2]?.[3]?.[14]?.[0]?.[0]) ownerResponse = reviewItem[2][3][14][0][0];
    if (reviewItem[2]?.[2]?.[0]?.[0]) rating = reviewItem[2][2][0][0];

    if (reviewText || ownerResponse || rating > 0) {
      allReviews.push({ placeName, placeAddress, placeMapLink, rating, text: reviewText, ownerResponse, photoUrl });
    }
  });

  // Extrair fotos de googleData[22][1]
  photosList.forEach((item) => {
    let photoUrl = null;
    let placeName = "Local desconhecido";
    let placeAddress = "";
    let placeMapLink = null;

    if (item[0]?.[6]?.[0]) photoUrl = item[0][6][0];
    if (item[1]?.[2]) placeName = item[1][2];
    if (item[1]?.[3]) placeAddress = item[1][3];
    if (item[1]?.[17]?.[0]) placeMapLink = item[1][17][0];

    if (photoUrl) {
      allPhotos.push({ url: photoUrl, placeName, placeAddress, placeMapLink });
    }
  });

  const reviewsCount = contributionStats.find(c => c.type === "Avaliações")?.count || allReviews.length;
  const photosCount = contributionStats.find(c => c.type === "Fotos")?.count || allPhotos.length;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className="bg-[#111] border-gray-800 w-[95vw] max-w-[500px] md:max-w-[900px] lg:max-w-[1100px] max-h-[90vh] p-0 overflow-hidden rounded-2xl" 
          data-testid="google-data-modal"
          showClose={false}
        >
          <DialogDescription className="sr-only">Dados do Google do usuário</DialogDescription>
          
          {/* Header - Responsivo */}
          <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 p-4 md:p-6 relative">
            <button
              onClick={() => onOpenChange(false)}
              className="absolute top-3 right-3 md:top-4 md:right-4 p-1.5 md:p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors z-10"
              data-testid="close-modal-btn"
            >
              <X className="h-4 w-4 md:h-5 md:w-5 text-white" />
            </button>
            
            <div className="flex items-center gap-3 md:gap-5">
              {userAvatar ? (
                <img src={userAvatar} alt={userName} className="h-14 w-14 md:h-20 md:w-20 rounded-full border-2 md:border-4 border-white/30 object-cover" />
              ) : (
                <div className="h-14 w-14 md:h-20 md:w-20 rounded-full bg-white/20 flex items-center justify-center">
                  <User className="h-7 w-7 md:h-10 md:w-10 text-white/70" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-2xl font-bold text-white truncate">{userName}</h2>
                <p className="text-white/70 text-xs md:text-sm truncate">{email}</p>
                
                {/* Stats - Desktop inline, Mobile abaixo */}
                <div className="hidden md:flex gap-3 mt-3">
                  <Badge className="bg-white/15 text-white border-0 text-sm px-3 py-1">
                    <Award className="h-4 w-4 mr-1.5" />
                    {levelName}
                  </Badge>
                  <Badge className="bg-white/15 text-white border-0 text-sm px-3 py-1">
                    <TrendingUp className="h-4 w-4 mr-1.5" />
                    {totalPoints.toLocaleString()} pontos
                  </Badge>
                  <Badge className="bg-white/15 text-white border-0 text-sm px-3 py-1">
                    <MessageSquare className="h-4 w-4 mr-1.5" />
                    {reviewsCount} reviews
                  </Badge>
                  <Badge className="bg-white/15 text-white border-0 text-sm px-3 py-1">
                    <Camera className="h-4 w-4 mr-1.5" />
                    {photosCount} fotos
                  </Badge>
                </div>
              </div>
            </div>
            
            {/* Stats row - Mobile only */}
            <div className="flex gap-2 mt-3 md:hidden flex-wrap">
              <Badge className="bg-white/15 text-white border-0 text-xs px-2 py-0.5">
                <Award className="h-3 w-3 mr-1" />
                {levelName}
              </Badge>
              <Badge className="bg-white/15 text-white border-0 text-xs px-2 py-0.5">
                <TrendingUp className="h-3 w-3 mr-1" />
                {totalPoints.toLocaleString()} pts
              </Badge>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={selectedTab} onValueChange={setSelectedTab} className="flex-1 flex flex-col">
            <TabsList className="grid grid-cols-3 bg-[#1a1a1a] rounded-none border-b border-gray-800 h-11 md:h-14">
              <TabsTrigger
                value="reviews"
                className="data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400 rounded-none text-xs md:text-sm gap-1 md:gap-2"
              >
                <MessageSquare className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Reviews</span>
                <span className="text-[10px] md:text-xs opacity-70">({allReviews.length})</span>
              </TabsTrigger>
              <TabsTrigger
                value="photos"
                className="data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400 rounded-none text-xs md:text-sm gap-1 md:gap-2"
              >
                <Camera className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Fotos</span>
                <span className="text-[10px] md:text-xs opacity-70">({allPhotos.length})</span>
              </TabsTrigger>
              <TabsTrigger
                value="stats"
                className="data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-400 rounded-none text-xs md:text-sm gap-1 md:gap-2"
              >
                <Star className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Stats</span>
              </TabsTrigger>
            </TabsList>

            {/* Reviews Tab */}
            <TabsContent value="reviews" className="flex-1 overflow-auto m-0 max-h-[calc(90vh-200px)] md:max-h-[calc(90vh-220px)]" style={{ width: '100%', maxWidth: '100%' }}>
              {allReviews.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <MessageSquare className="h-10 w-10 mb-2 opacity-50" />
                  <p className="text-sm">Nenhuma review encontrada</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-800/50 md:grid md:grid-cols-2 md:divide-y-0 md:gap-4 md:p-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                  {allReviews.map((review, index) => (
                    <div 
                      key={index} 
                      className="p-3 md:p-4 hover:bg-white/[0.02] transition-colors md:bg-[#1a1a1a] md:rounded-xl md:border md:border-gray-800"
                      style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}
                    >
                      <div className="flex gap-3 w-full max-w-full" style={{ overflow: 'hidden' }}>
                        {review.photoUrl && (
                          <img
                            src={review.photoUrl}
                            alt=""
                            className="w-14 h-14 md:w-20 md:h-20 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0 w-full max-w-full" style={{ overflow: 'hidden' }}>
                          <div className="flex items-start justify-between gap-2 w-full max-w-full mb-1" style={{ overflow: 'hidden' }}>
                            <h4 
                              className="text-sm md:text-base font-semibold text-white flex-1 min-w-0" 
                              style={{ 
                                wordBreak: 'break-word', 
                                overflowWrap: 'break-word',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {review.placeName}
                            </h4>
                            {review.rating > 0 && (
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <Star key={i} className={`h-3 w-3 md:h-4 md:w-4 ${i < review.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-700"}`} />
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {review.text && (
                            <p 
                              className="text-xs md:text-sm text-gray-400 mt-1 w-full max-w-full" 
                              style={{ 
                                wordBreak: 'break-word', 
                                overflowWrap: 'break-word', 
                                hyphens: 'auto',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {review.text}
                            </p>
                          )}
                          
                          {review.ownerResponse && (
                            <div className="mt-2 pl-2 border-l-2 border-blue-500/50 w-full max-w-full" style={{ overflow: 'hidden', maxWidth: '100%' }}>
                              <p className="text-[10px] md:text-xs text-blue-400 font-medium">Resposta:</p>
                              <p 
                                className="text-xs md:text-sm text-gray-500 w-full max-w-full" 
                                style={{ 
                                  wordBreak: 'break-word', 
                                  overflowWrap: 'break-word', 
                                  hyphens: 'auto',
                                  overflow: 'hidden',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical'
                                }}
                              >
                                {review.ownerResponse}
                              </p>
                            </div>
                          )}
                          
                          {review.placeMapLink && (
                            <button
                              onClick={() => window.open(review.placeMapLink, '_blank')}
                              className="flex items-center gap-1 text-[10px] md:text-xs text-blue-400 hover:text-blue-300 mt-2"
                            >
                              <MapPin className="h-3 w-3" />
                              Ver no Maps
                              <ChevronRight className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Photos Tab */}
            <TabsContent value="photos" className="flex-1 overflow-auto m-0 max-h-[calc(90vh-200px)] md:max-h-[calc(90vh-220px)]">
              {allPhotos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Camera className="h-10 w-10 mb-2 opacity-50" />
                  <p className="text-sm">Nenhuma foto encontrada</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-0.5 md:gap-2 md:p-4">
                  {allPhotos.map((photo, index) => (
                    <div
                      key={index}
                      className="aspect-square relative group overflow-hidden md:rounded-lg"
                    >
                      <img
                        src={photo.url}
                        alt={photo.placeName}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end">
                        <div className="hidden md:block w-full p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-white text-xs truncate">{photo.placeName}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Stats Tab */}
            <TabsContent value="stats" className="flex-1 overflow-auto m-0 p-4 md:p-6 max-h-[calc(90vh-200px)] md:max-h-[calc(90vh-220px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {contributionStats.filter(s => s.count > 0).map((stat, index) => (
                  <div key={index} className="flex items-center justify-between p-3 md:p-4 bg-[#1a1a1a] rounded-xl border border-gray-800">
                    <span className="text-sm md:text-base text-gray-400">{stat.type}</span>
                    <span className="text-lg md:text-2xl font-bold text-white">{stat.count.toLocaleString()}</span>
                  </div>
                ))}
                
                {contributionStats.filter(s => s.count > 0).length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <TrendingUp className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Sem estatísticas disponíveis</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoogleDataModal;
